// src/features/support/pages/TicketPage.tsx

import React, { useCallback, useState } from "react";
import { ManualAttachmentInput } from "../components/ManualAttachmentInput";
import { useTicketDraftStore } from "../store/ticketDraftStore";
import { submitTicket } from "../api/ticketApi";
import { PageBanner } from "@/shared/components/PageBanner";

/**
 * ADR-110: reachable two ways —
 *  1. Directly, by navigating here without any prior capture (attachments
 *     queue starts empty; user may still add manual attachments).
 *  2. Via TicketCaptureButton, which pre-populates the draft queue with
 *     one auto-masked "direct" screenshot before routing here.
 */
export const TicketPage: React.FC = () => {
  const { attachments, removeAttachment, clear } = useTicketDraftStore();
  const [subject, setSubject] = useState("");
  const [description, setDescription] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const handleSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      setSubmitting(true);
      setError(null);
      setSuccess(false);
      try {
        await submitTicket({ subject, description, attachments });
        clear();
        setSubject("");
        setDescription("");
        setSuccess(true);
      } catch (err) {
        setError("ثبت تیکت با خطا مواجه شد. لطفاً دوباره تلاش کنید.");
      } finally {
        setSubmitting(false);
      }
    },
    [subject, description, attachments, clear]
  );

  return (
    <div
      dir="rtl"
      style={{
        padding: "0.5rem 0",
        maxWidth: "100%",
        margin: "0",
        display: "flex",
        flexDirection: "column",
        gap: "1.5rem",
        fontFamily: "var(--font-family-fa, sans-serif)",
      }}
    >
      <PageBanner
        title="ارسال تیکت پشتیبانی جدید"
        subtitle="ثبت درخواست، گزارش خطا یا ارسال اسکرین‌شات به تیم پشتیبانی فنی"
        icon="🎫"
      >
        <div style={{ display: "flex", gap: "0.75rem", alignItems: "center" }}>
          {attachments.length > 0 && (
            <span
              style={{
                padding: "0.4rem 1rem",
                borderRadius: "12px",
                background: "rgba(255, 255, 255, 0.2)",
                color: "#FFFFFF",
                fontSize: "0.85rem",
                fontWeight: 600,
              }}
            >
              📎 {attachments.length} تصویر پیوست‌شده
            </span>
          )}
        </div>
      </PageBanner>

      <div
        className="brand-card-extand"
        style={{
          padding: "1.75rem",
          maxWidth: "760px",
          margin: "0 auto",
          width: "100%",
        }}
      >
        <div
          style={{
            paddingBottom: "1rem",
            marginBottom: "1.5rem",
            borderBottom: "1px solid var(--color-border, #E4DCC8)",
            display: "flex",
            alignItems: "center",
            gap: "8px",
          }}
        >
          <span style={{ fontSize: "1.2rem" }}>📝</span>
          <h3 style={{ margin: 0, fontSize: "1.1rem", fontWeight: 800, color: "#007270" }}>
            مشخصات درخواست پشتیبانی
          </h3>
        </div>

        {success && (
          <div
            style={{
              padding: "0.85rem 1.1rem",
              background: "rgba(16, 185, 129, 0.12)",
              border: "1px solid #10B981",
              color: "#065F46",
              borderRadius: "10px",
              fontWeight: 700,
              fontSize: "0.85rem",
              marginBottom: "1.25rem",
            }}
          >
            ✓ تیکت پشتیبانی با موفقیت ثبت شد و به تیم فنی ارجاع گردید.
          </div>
        )}

        <form onSubmit={handleSubmit} data-testid="ticket-form" style={{ display: "flex", flexDirection: "column", gap: "1.25rem" }}>
          <div>
            <label
              style={{
                fontSize: "0.85rem",
                fontWeight: 700,
                color: "#2B2B2B",
                display: "block",
                marginBottom: "6px",
              }}
            >
              موضوع تیکت <span style={{ color: "#EF4444" }}>*</span>
            </label>
            <input
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              required
              data-testid="ticket-subject-input"
              placeholder="عنوان یا خلاصه درخواست را وارد کنید..."
              style={{
                width: "100%",
                padding: "10px 14px",
                borderRadius: "10px",
                border: "1px solid var(--color-border, #E4DCC8)",
                background: "#FFFFFF",
                color: "#2B2B2B",
                fontSize: "0.9rem",
                outline: "none",
              }}
            />
          </div>

          <div>
            <label
              style={{
                fontSize: "0.85rem",
                fontWeight: 700,
                color: "#2B2B2B",
                display: "block",
                marginBottom: "6px",
              }}
            >
              شرح کامل مشکل یا درخواست
            </label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              data-testid="ticket-description-input"
              rows={5}
              placeholder="جزئیات خطا، صفحه مربوطه، شماره سند یا مراحل ایجاد مشکل را توضیح دهید..."
              style={{
                width: "100%",
                padding: "10px 14px",
                borderRadius: "10px",
                border: "1px solid var(--color-border, #E4DCC8)",
                background: "#FFFFFF",
                color: "#2B2B2B",
                fontSize: "0.9rem",
                outline: "none",
                resize: "vertical",
                minHeight: "120px",
              }}
            />
          </div>

          <div>
            <label
              style={{
                fontSize: "0.85rem",
                fontWeight: 700,
                color: "#2B2B2B",
                display: "block",
                marginBottom: "6px",
              }}
            >
              تصاویر و فایل‌های پیوست‌شده ({attachments.length})
            </label>

            <ul
              data-testid="draft-attachments-list"
              style={{
                listStyle: "none",
                padding: 0,
                margin: attachments.length > 0 ? "0 0 10px 0" : "0",
                display: "flex",
                flexDirection: "column",
                gap: "8px",
              }}
            >
              {attachments.map((a) => (
                <li
                  key={a.id}
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    padding: "10px 14px",
                    borderRadius: "10px",
                    background: "rgba(0, 0, 0, 0.02)",
                    border: "1px solid var(--color-border, #E4DCC8)",
                  }}
                >
                  <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                    <span style={{ fontSize: "1.2rem" }}>📎</span>
                    <span style={{ fontWeight: 600, fontSize: "0.85rem", color: "#2B2B2B" }}>{a.filename}</span>
                    <span
                      style={{
                        padding: "2px 8px",
                        borderRadius: "6px",
                        fontSize: "0.75rem",
                        fontWeight: 700,
                        background:
                          a.captureMethod === "direct"
                            ? "rgba(0, 114, 112, 0.1)"
                            : "rgba(245, 158, 11, 0.1)",
                        color: a.captureMethod === "direct" ? "#007270" : "#B45309",
                      }}
                    >
                      {a.captureMethod === "direct" ? "مستقیم/سانسورشده" : "دستی"}
                    </span>
                  </div>
                  <button
                    type="button"
                    onClick={() => removeAttachment(a.id)}
                    style={{
                      padding: "4px 12px",
                      borderRadius: "6px",
                      border: "1px solid #EF4444",
                      background: "rgba(239, 68, 68, 0.08)",
                      color: "#DC2626",
                      fontSize: "0.75rem",
                      fontWeight: 700,
                      cursor: "pointer",
                      transition: "background 0.2s",
                    }}
                  >
                    حذف
                  </button>
                </li>
              ))}
            </ul>

            <ManualAttachmentInput />
          </div>

          {error && (
            <p
              role="alert"
              style={{
                padding: "0.85rem 1.1rem",
                background: "rgba(239, 68, 68, 0.12)",
                border: "1px solid #EF4444",
                color: "#B91C1C",
                borderRadius: "10px",
                fontWeight: 600,
                fontSize: "0.85rem",
                margin: 0,
              }}
            >
              {error}
            </p>
          )}

          <div
            style={{
              display: "flex",
              justifyContent: "flex-end",
              gap: "10px",
              marginTop: "0.5rem",
              paddingTop: "1.25rem",
              borderTop: "1px solid var(--color-border, #E4DCC8)",
            }}
          >
            <button
              type="submit"
              disabled={submitting}
              data-testid="ticket-submit-button"
              style={{
                padding: "10px 32px",
                borderRadius: "10px",
                background: "#007270",
                color: "#FFFFFF",
                border: "none",
                fontSize: "0.95rem",
                fontWeight: 800,
                cursor: submitting ? "not-allowed" : "pointer",
                boxShadow: "0 4px 12px rgba(0, 114, 112, 0.25)",
                transition: "all 0.2s ease",
              }}
            >
              {submitting ? "در حال ارسال..." : "ثبت تیکت"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default TicketPage;
